import React, { useState } from 'react';

import { BarcodeOutlined } from '@ant-design/icons';
import { Button, Tooltip, Card, Typography, Space, Divider } from 'antd';
import JsBarcode from 'jsbarcode';
import { useTranslation } from 'react-i18next';

import { SPECIAL_BARCODES } from '@constants/specialBarcodes';
import { useBarcodeInputContext } from '@contexts/operator/BarcodeInputContext';

const { Text } = Typography;

/**
 * GlobalBarcodeInput
 *
 * 全域條碼輸入組件，應放置在 Layout 層級。
 * 配合 BarcodeInputContext 使用，提供全域鍵盤監聽功能。
 *
 * @param {Object} props
 * @param {string} props.placeholder - Input placeholder
 * @param {string[]} props.barcodeShortcuts - 啟用的特殊條碼 key 列表（例如 ['DO_PACKING', 'CONFIRM_PRINT']）
 * @param {string} props.className - 額外的 CSS class
 */
export default function GlobalBarcodeInput({
  placeholder,
  barcodeShortcuts = [],
  className = '',
}) {
  const { t } = useTranslation();
  const { inputRef, inputValue, submitBarcode } = useBarcodeInputContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const barcodeRefs = React.useRef({});

  const filteredBarcodes = barcodeShortcuts.length > 0
    ? Object.fromEntries(Object.entries(SPECIAL_BARCODES).filter(([key]) => barcodeShortcuts.includes(key)))
    : {};
  const hasShortcuts = Object.keys(filteredBarcodes).length > 0;

  // 生成條碼圖案
  React.useEffect(() => {
    if (!isExpanded || !hasShortcuts) return;

    Object.entries(filteredBarcodes).forEach(([key, barcode]) => {
      const canvas = barcodeRefs.current[key];
      if (!canvas) return;

      try {
        JsBarcode(canvas, barcode.code, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: true,
          fontSize: 14,
          margin: 8,
          background: '#f5f5f5',
        });
      } catch (err) {
        console.error('Failed to generate barcode:', err);
      }
    });
  }, [isExpanded, hasShortcuts, filteredBarcodes]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (inputRef.current?.value) {
        submitBarcode(inputRef.current.value);
        inputRef.current.value = '';
      }
    }
  };

  return (
    <div className={`global-barcode-input ${className}`} style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          ref={inputRef}
          type="text"
          className="qc-page__barcode-input"
          placeholder={placeholder || t('operator.common.barcode_placeholder', { defaultValue: '請掃描或輸入條碼' })}
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          aria-label={t('operator.common.barcode_input', { defaultValue: '條碼輸入' })}
          onKeyDown={handleKeyDown}
        />
        {hasShortcuts && (
          <Tooltip
            title={t('operator.qc.special_barcodes.button_tooltip', {
              defaultValue: isExpanded ? '隱藏特殊條碼' : '顯示特殊條碼',
            })}
          >
            <Button
              type={isExpanded ? 'primary' : 'default'}
              icon={<BarcodeOutlined />}
              onClick={() => setIsExpanded(!isExpanded)}
              style={{ flexShrink: 0 }}
            />
          </Tooltip>
        )}
      </div>

      {/* 展開的特殊條碼列表 */}
      {hasShortcuts && isExpanded && (
        <Card
          size="small"
          style={{
            marginTop: '8px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
          styles={{ body: { padding: '12px' } }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {Object.entries(filteredBarcodes).map(([key, barcode], index) => (
              <div key={key}>
                {index > 0 && <Divider style={{ margin: '8px 0' }} />}
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ color: '#1890ff', fontSize: '13px' }}>
                      {t(barcode.i18nKey, { defaultValue: barcode.label })}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                      - {t(`${barcode.i18nKey}_description`, { defaultValue: barcode.description })}
                    </Text>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '8px',
                      background: '#f5f5f5',
                      borderRadius: '4px',
                    }}
                  >
                    <canvas
                      ref={(el) => { barcodeRefs.current[key] = el; }}
                      width={300}
                      height={60}
                      style={{ maxWidth: '100%' }}
                    />
                  </div>
                </Space>
              </div>
            ))}
          </Space>
        </Card>
      )}
    </div>
  );
}
